import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useCallback } from 'react';
import useSelectedPatient from '../patient/useSelectedPatient';
import { useSetRecoilState } from 'recoil';
import useViewportEnvironment from '../useViewportEnvironment';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { Image3D } from '~/domain/patient/image3d';
import { exportableItemsCountState } from '~/state/atoms/viewerAtoms';

/**
 * A custom hook that provides a function to create a new empty structure set
 * for the currently selected patient.
 *
 * @returns A callback function to create a new structure set in the current patient.
 */
export default function useCreateStructureSet() {
    const primaryImage = useSelectedPrimaryImage();
    const patient = useSelectedPatient();
    // useSessionOrNull (not useStructureSetService): this hook can render
    // in the first commit, before PatientSessionProvider's effect has
    // created the session. useStructureSetService -> useSession would throw
    // on that render. The createStructureSet callback guards on session
    // below.
    const session = useSessionOrNull();
    const { setEnvironmentStructureSet } = useViewportEnvironment();
    const setExportableItemsCount = useSetRecoilState(
        exportableItemsCountState
    );
    const createStructureSet = useCallback(
        (
            /** Optional image to associate the new structure set with.
             * If undefined, the current primary image will be used. */
            image: Image3D | undefined = undefined,
            /** True if newly created structure set should immediately be set active, false otherwise.
             * Action is not performed if the target image is not current primary image. Defaults to true. */
            setCreatedActive = true
        ) => {
            const targetImage = image || primaryImage;

            if (!patient) {
                console.error(
                    'Cannot create structure set: No patient selected'
                );
                return;
            }

            if (!targetImage) {
                console.error(
                    'Cannot create structure set: No primary image selected'
                );
                return;
            }

            if (!session) {
                console.error(
                    'Cannot create structure set: No active patient session'
                );
                return;
            }

            // Orchestration lives on the domain service: create the
            // DicomStructureSet skeleton, build the StructureSet wired
            // to its source image, and add to the patient aggregate
            // inside a tracked batch.
            const newStructureSet =
                session.structureSetService.createEmpty(targetImage);

            setExportableItemsCount((prevCount) => prevCount + 1);

            if (setCreatedActive && targetImage === primaryImage) {
                setEnvironmentStructureSet(
                    [newStructureSet],
                    patient.patientId
                );
            }
        },
        [
            primaryImage,
            patient,
            session,
            setExportableItemsCount,
            setEnvironmentStructureSet,
        ]
    );

    return createStructureSet;
}
